import { apiFetch } from './api';
import type { EnrichedProduct } from '../types/enriched-product';

// ─── TYPES ──────────────────────────────────────────────────────────────────

export interface NetworkDevice {
  ip: string;
  mac?: string;
  hostname?: string;
  vendor?: string;
  deviceType?: string;
  manufacturer?: string;
  model?: string;
  firstSeen: string;
  lastSeen: string;
  isOnline: boolean;
}

export interface NetworkScanResult {
  scanId: string;
  devicesFound: number;
  devices: NetworkDevice[];
  scanDuration: number;
}

/** @deprecated Use BarcodeProductResult instead — aligns with app and EnrichedProduct shape. */
export interface BarcodeLookupResult {
  found: boolean;
  upc?: string;
  brand?: string;
  model?: string;
  productName?: string;
  category?: string;
  manufacturer?: string;
  imageUrl?: string;
  description?: string;
}

// ─── NETWORK DISCOVERY API ─────────────────────────────────────────────────

/**
 * Scan the network for devices
 */
export async function scanNetwork(): Promise<NetworkScanResult> {
  const response = await apiFetch<{ success: boolean; data: NetworkScanResult }>(
    '/v1/discovery/enrich',
    {
      method: 'POST',
      body: {},
    }
  );
  return response.data;
}

/**
 * Get network scan history
 */
export async function getNetworkHistory(): Promise<NetworkScanResult[]> {
  const response = await apiFetch<{ success: boolean; data: NetworkScanResult[] }>(
    '/v1/discovery/history'
  );
  return response.data;
}

/**
 * Register a network device as an asset
 */
export async function registerDeviceAsAsset(
  homeId: string,
  device: NetworkDevice
): Promise<{ id: string; name: string }> {
  const response = await apiFetch<{ success: boolean; data: { id: string; name: string } }>('/v1/discovery/register', {
    method: 'POST',
    body: {
      homeId,
      ip: device.ip,
      mac: device.mac,
      hostname: device.hostname,
      vendor: device.vendor,
      deviceType: device.deviceType,
    },
  });
  return response.data;
}

// ─── BARCODE LOOKUP API ────────────────────────────────────────────────────

/**
 * Shared response shape for all barcode/product lookup calls.
 * Maps to the canonical `EnrichedProduct` the backend returns in `data.product`.
 */
export interface BarcodeProductResult {
  product: EnrichedProduct | null;
  found: boolean;
}

function toBarcodeProductResult(data: Record<string, unknown>): BarcodeProductResult {
  const product = (data.product ?? null) as EnrichedProduct | null;
  return { product, found: !!product };
}

/**
 * Look up a product by its barcode / UPC / EAN.
 * Payload: { barcode }  — mirrors app's lookupBarcode().
 */
export async function lookupBarcode(barcode: string): Promise<BarcodeProductResult> {
  const clean = barcode.trim();
  const response = await apiFetch<{ success: boolean; data: Record<string, unknown> }>(
    '/v1/ai/barcode-lookup',
    {
      method: 'POST',
      body: { barcode: clean },
    }
  );
  return toBarcodeProductResult(response.data);
}

/**
 * Look up a product by its MPN (Manufacturer Part Number / model number).
 * Payload: { mpn, manufacturer? }  — mirrors app's lookupByMpn().
 */
export async function lookupByMpn(
  mpn: string,
  manufacturer?: string,
): Promise<BarcodeProductResult> {
  const clean = mpn.trim();
  const response = await apiFetch<{ success: boolean; data: Record<string, unknown> }>(
    '/v1/ai/barcode-lookup',
    {
      method: 'POST',
      body: {
        mpn: clean,
        ...(manufacturer?.trim() ? { manufacturer: manufacturer.trim() } : {}),
      },
    }
  );
  return toBarcodeProductResult(response.data);
}

/**
 * Search for a product by keyword query (brand + model name, e.g. "Samsung RF29A9071SR").
 * Payload: { search }  — mirrors app's lookupBySearch().
 */
export async function lookupBySearch(query: string): Promise<BarcodeProductResult> {
  const clean = query.trim();
  const response = await apiFetch<{ success: boolean; data: Record<string, unknown> }>(
    '/v1/ai/barcode-lookup',
    {
      method: 'POST',
      body: { search: clean },
    }
  );
  return toBarcodeProductResult(response.data);
}

// ─── IDENTIFY PRODUCT ────────────────────────────────────────────────────────

/**
 * AI identifies what a scanned value is (barcode / MPN / serial / model) and
 * returns full product info.  This mirrors the Flutter app's identifyProduct().
 *
 * Payload:  { scannedValue, assetType?, detectedBrand? }
 * Response: { product: EnrichedProduct | null, inputType: string, found: boolean }
 */
export interface IdentifyProductResult {
  product: EnrichedProduct | null;
  /** What the AI determined the scanned value to be (e.g. 'barcode', 'mpn', 'serial') */
  inputType: string;
  found: boolean;
}

export async function identifyProduct(
  scannedValue: string,
  assetType?: string,
  detectedBrand?: string,
): Promise<IdentifyProductResult> {
  const response = await apiFetch<{ success: boolean; data: Record<string, unknown> }>(
    '/v1/ai/identify-product',
    {
      method: 'POST',
      body: {
        scannedValue: scannedValue.trim(),
        ...(assetType?.trim() ? { assetType: assetType.trim() } : {}),
        ...(detectedBrand?.trim() ? { detectedBrand: detectedBrand.trim() } : {}),
      },
    }
  );
  const d = response.data;
  const product = (d.product ?? null) as EnrichedProduct | null;
  return {
    product,
    inputType: (d.inputType as string) ?? 'unknown',
    found: !!product,
  };
}
